/**
 * Admin inspiration archive (/admin/ideas).
 * Shows the year/month accordion + a "+ New article" toolbar that pops a tiny
 * inline form (title + year + month). Created article opens straight into the
 * editor.
 */
import { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Plus } from "@phosphor-icons/react";
import { inspirationsAdminApi } from "../../api/inspirations";
import { MONTH_KEYS, now } from "../../lib/dates";
import PortalPageHeader from "../../components/portal/PortalPageHeader";
import InspirationArchive from "../../components/inspirations/InspirationArchive";

export default function AdminInspirations() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const cur = useMemo(() => now(), []);
  const [title, setTitle] = useState("");
  const [year, setYear] = useState(cur.year);
  const [month, setMonth] = useState(cur.month);
  const [saving, setSaving] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  const yearOptions = useMemo(() => {
    const list = [];
    for (let y = cur.year + 1; y >= cur.year - 4; y--) list.push(y);
    return list;
  }, [cur.year]);

  const submit = useCallback(
    async (e) => {
      e.preventDefault();
      const trimmed = title.trim();
      if (!trimmed) return;
      setSaving(true);
      try {
        const created = await inspirationsAdminApi.create(trimmed, year, month);
        setTitle("");
        setReloadKey((k) => k + 1);
        navigate(`/admin/ideas/${created.id}`);
      } finally {
        setSaving(false);
      }
    },
    [title, year, month, navigate],
  );

  return (
    <div data-testid="admin-inspirations-page" className="space-y-10">
      <PortalPageHeader
        kickerKey="admin.placeholder.kicker"
        titleKey="admin.nav.ideas"
        subtitleKey="admin.pages.ideas_desc"
      />

      {/* New article toolbar */}
      <form
        onSubmit={submit}
        data-testid="admin-inspiration-new-form"
        className="flex flex-col md:flex-row md:items-center gap-2 rounded-2xl border-2 border-dashed border-slate-200 bg-white px-4 py-3"
      >
        <Plus size={18} weight="bold" className="text-slate-400 shrink-0" />
        <input
          type="text"
          data-testid="admin-inspiration-new-title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder={t("inspiration.new_article_placeholder")}
          className="flex-1 bg-transparent outline-none text-sm placeholder:text-slate-400 min-w-[200px]"
        />
        <select
          data-testid="admin-inspiration-new-month"
          value={month}
          onChange={(e) => setMonth(parseInt(e.target.value, 10))}
          className="bg-transparent text-sm border border-slate-200 rounded-full px-3 py-1.5 capitalize"
        >
          {MONTH_KEYS.map((k, i) => (
            <option key={i} value={i + 1}>
              {t(k)}
            </option>
          ))}
        </select>
        <select
          data-testid="admin-inspiration-new-year"
          value={year}
          onChange={(e) => setYear(parseInt(e.target.value, 10))}
          className="bg-transparent text-sm border border-slate-200 rounded-full px-3 py-1.5"
        >
          {yearOptions.map((y) => (
            <option key={y} value={y}>
              {y}
            </option>
          ))}
        </select>
        <button
          type="submit"
          data-testid="admin-inspiration-new-submit"
          disabled={saving || !title.trim()}
          className="rounded-full bg-slate-900 px-4 py-2 text-xs font-medium text-white disabled:opacity-40 hover:bg-slate-800"
        >
          {t("inspiration.create")}
        </button>
      </form>

      <InspirationArchive
        mode="admin"
        basePath="/admin/ideas"
        reloadKey={reloadKey}
      />
    </div>
  );
}
